import {
  ArrowClockwise,
  ArrowLeft,
  ArrowRight,
  ChartBar,
  CheckCircle,
  ClipboardText,
  MagnifyingGlass,
  LockSimple,
  Users,
} from "@phosphor-icons/react";

const STEPS = [
  { key: "transcript", label: "Transcript", icon: ClipboardText },
  { key: "analysis", label: "Dissection", icon: MagnifyingGlass },
  { key: "coaching", label: "Coaching", icon: ChartBar },
  { key: "notes", label: "CRM note", desktopLabel: "CRM Note", icon: ClipboardText },
  { key: "relationship", label: "Relationship", icon: Users },
];

export default function LiveDemoFlowNav({
  activeStep,
  completedSteps = [],
  busyStep = null,
  maxUnlockedIndex = 0,
  onStepSelect,
  onRestart,
}) {
  const activeIndex = Math.max(0, STEPS.findIndex((step) => step.key === activeStep));
  const previous = activeIndex > 0 ? STEPS[activeIndex - 1] : null;
  const next = activeIndex < STEPS.length - 1 ? STEPS[activeIndex + 1] : null;
  const completed = new Set(completedSteps);
  const active = STEPS[activeIndex] || STEPS[0];
  const nextUnlocked = Boolean(next);
  const clampedUnlockedIndex = Math.max(0, Math.min(STEPS.length - 1, Number(maxUnlockedIndex) || 0));

  const select = (key) => {
    if (!key || busyStep || typeof onStepSelect !== "function") return;
    onStepSelect(key);
  };

  const selectDesktop = (key) => {
    const index = STEPS.findIndex((step) => step.key === key);
    const isRelationship = key === "relationship";
    if (index < 0 || (!isRelationship && index > clampedUnlockedIndex)) return;
    select(key);
  };

  return (
    <nav className="live-demo-flow is-exploration" aria-label="Interactive demo sections">
      <div className="live-demo-flow__hint live-demo-flow__hint--mobile-legacy">Explore any section. The primary action below continues the guided path.</div>
      <div className="live-demo-flow__desktop" role="tablist" aria-label="Interactive demo flow">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isActive = step.key === activeStep;
          const isComplete = completed.has(step.key);
          const isBusy = busyStep === step.key;
          const isUnlocked = step.key === "relationship" || index <= clampedUnlockedIndex;

          return (
            <button
              key={step.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-busy={isBusy || undefined}
              aria-disabled={!isUnlocked || undefined}
              className={`live-demo-flow__step${isActive ? " is-active" : ""}${isComplete ? " is-complete" : ""}${!isUnlocked ? " is-locked" : ""}`}
              onClick={() => selectDesktop(step.key)}
              disabled={Boolean(busyStep) || !isUnlocked}
            >
              <span className="live-demo-flow__number">{index + 1}</span>
              <Icon size={16} weight={isActive ? "fill" : "duotone"} aria-hidden="true" />
              <span>{isBusy ? "Preparing…" : (step.desktopLabel || step.label)}</span>
              {!isUnlocked && !isBusy && <LockSimple size={12} weight="fill" aria-hidden="true" />}
              {isComplete && !isBusy && <CheckCircle size={13} weight="fill" aria-label="Visited" />}
            </button>
          );
        })}

        {onRestart && (
          <button
            type="button"
            className="live-demo-flow__restart"
            onClick={onRestart}
            disabled={Boolean(busyStep)}
          >
            <ArrowClockwise size={14} weight="bold" aria-hidden="true" />
            Restart
          </button>
        )}
      </div>

      <div className="live-demo-flow__mobile">
        <button
          type="button"
          className="live-demo-flow__mobile-button"
          onClick={() => previous && select(previous.key)}
          disabled={!previous || Boolean(busyStep)}
          aria-label={previous ? `Previous: ${previous.label}` : "No previous step"}
        >
          <ArrowLeft size={18} weight="bold" aria-hidden="true" />
        </button>

        <div className="live-demo-flow__mobile-current" aria-current="step">
          <small>{activeIndex + 1} of {STEPS.length}</small>
          <strong>{busyStep === active.key ? "Preparing…" : active.label}</strong>
        </div>

        <button
          type="button"
          className="live-demo-flow__mobile-button is-next"
          onClick={() => next && select(next.key)}
          disabled={!next || Boolean(busyStep) || !nextUnlocked}
          aria-label={next ? (nextUnlocked ? `Next: ${next.label}` : `Complete ${active.label} first`) : "Demo complete"}
        >
          <ArrowRight size={18} weight="bold" aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
}
